import { AlertTriangle, RotateCw, X } from 'lucide-react';

/**
 * The files that didn't upload, named, with the reason the server or the
 * browser gave.
 *
 * This exists because the flow used to reduce failures to a count — "2 file(s)
 * failed to upload" — and then drop the files from state, so there was nothing
 * to retry and no way to tell which receipt was missing. A customer hitting it
 * could only report "sometimes it doesn't work", which is exactly how the HEIC
 * bug stayed invisible.
 */
export interface UploadFailureListProps {
  failures: { file: File; reason: string }[];
  onRetry: () => void;
  onDismiss: () => void;
  /** Retry is pointless while a batch is already in flight. */
  busy?: boolean;
}

export function UploadFailureList({ failures, onRetry, onDismiss, busy }: UploadFailureListProps) {
  if (failures.length === 0) return null;

  return (
    <div
      className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 sm:p-4"
      // Announced rather than silent: on a phone this panel can render below
      // the fold, under the receipt that did upload.
      role="alert"
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-red-800">
            {failures.length === 1
              ? "1 receipt didn't upload"
              : `${failures.length} receipts didn't upload`}
          </p>
          <ul className="mt-2 space-y-1.5">
            {failures.map((failure, i) => (
              // File names repeat across a batch (IMG_0001.HEIC from two phones),
              // so the index is part of the key.
              <li key={`${failure.file.name}-${i}`} className="text-sm text-red-700">
                <span className="break-all font-medium">{failure.file.name}</span>
                <span className="text-red-600"> — {failure.reason}</span>
              </li>
            ))}
          </ul>
          <button
            type="button"
            onClick={onRetry}
            disabled={busy}
            className="mt-3 inline-flex min-h-[44px] items-center gap-1.5 text-sm font-medium text-red-700 underline hover:text-red-900 disabled:opacity-50 sm:min-h-0"
          >
            <RotateCw className="h-4 w-4" aria-hidden="true" />
            {failures.length === 1 ? 'Try again' : 'Try all again'}
          </button>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="shrink-0 rounded p-1 text-red-500 hover:bg-red-100 hover:text-red-700"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
